import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { ChatBot } from '../ChatBot';
import { NotificationBell } from '../NotificationBell';
import { OnboardingWizard } from '../OnboardingWizard';
import {
  LayoutDashboard,
  Ship,
  Users,
  Building2,
  Truck,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Eye,
  EyeOff,
  Package,
  Shield,
  Wallet,
  DollarSign,
  Bell,
  History,
  ShieldCheck,
  Radar,
  Inbox,
  FileBarChart,
  Timer,
  CreditCard,
  BookOpen,
  Sparkles,
  BarChart3,
} from 'lucide-react';

// Nav items for SUPER ADMIN (OFIMEX platform owner) - ONLY SaaS management
const superAdminNavItems = [
  { path: '/super-admin', label: 'Dashboard SaaS', icon: Shield },
  { path: '/super-admin/audit-logs', label: 'Audit Logs', icon: History },
  { path: '/arnulfo', label: 'Arnulfo', icon: DollarSign },
  { path: '/ai-arancel', labelKey: 'nav.aiArancel', icon: Sparkles },
  { path: '/manuals', labelKey: 'manual.sidebarLink', icon: BookOpen },
];

// Nav items for regular org users (operators, admins, clients)
// `module` maps to the permissions.modules key for granular per-user access control
const navItems = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, roles: ['admin', 'ops'], feature: 'dashboard', module: 'dashboard' },
  { path: '/my-dashboard', labelKey: 'nav.myDashboard', icon: LayoutDashboard, roles: ['client'], feature: 'dashboard', module: 'dashboard' },
  { path: '/shipments', labelKey: 'nav.shipments', icon: Ship, roles: ['admin', 'ops', 'client'], feature: 'shipments', module: 'shipments' },
  { path: '/tracking', labelKey: 'nav.tracking', icon: Radar, roles: ['admin', 'ops'], feature: 'tracking_carriers', module: 'tracking' },
  { path: '/payments', labelKey: 'nav.payments', icon: Wallet, roles: ['admin', 'ops'], module: 'payments' },
  { path: '/my-payments', labelKey: 'nav.myPayments', icon: Wallet, roles: ['client'], module: 'payments' },
  { path: '/pricing', labelKey: 'nav.pricing', icon: DollarSign, roles: ['admin'], field: 'view_prices' },
  { path: '/certificates', labelKey: 'nav.certificates', icon: ShieldCheck, roles: ['admin', 'ops'], feature: 'certificates', module: 'certificates' },
  { path: '/alerts', labelKey: 'nav.alerts', icon: Bell, roles: ['admin'], feature: 'alerts', module: 'alerts' },
  { path: '/email-settings', labelKey: 'nav.email', icon: Inbox, roles: ['admin'] },
  { path: '/reports', labelKey: 'nav.reports', icon: FileBarChart, roles: ['admin'], feature: 'monthly_report', module: 'reports' },
  { path: '/sla', labelKey: 'nav.sla', icon: Timer, roles: ['admin', 'ops'], feature: 'sla_dashboard', module: 'sla' },
  { path: '/analytics/operations', labelKey: 'nav.operationsAnalytics', icon: BarChart3, roles: ['admin', 'ops'] },
  { path: '/clients', labelKey: 'nav.clients', icon: Users, roles: ['admin'], feature: 'client_portal', module: 'clients' },
  { path: '/importers', labelKey: 'nav.importers', icon: Building2, roles: ['admin', 'ops'], module: 'importers' },
  { path: '/suppliers', labelKey: 'nav.suppliers', icon: Truck, roles: ['admin', 'ops'], module: 'suppliers' },
  { path: '/catalogs', labelKey: 'nav.catalogs', icon: Package, roles: ['admin'], module: 'catalogs' },
  { path: '/users', labelKey: 'nav.users', icon: Users, roles: ['admin'], feature: 'multi_user', module: 'users' },
  { path: '/manuals', labelKey: 'manual.sidebarLink', icon: BookOpen, roles: ['admin', 'ops', 'client'] },
  { path: '/ai-arancel', labelKey: 'nav.aiArancel', icon: Sparkles, roles: ['admin', 'ops'] },
  { path: '/arnulfo', label: 'Arnulfo', icon: DollarSign, roles: ['admin'], feature: 'client_arnulfo' },
];

export function DashboardLayout({ children }) {
  const {
    user, logout, canManage, clientViewMode, setClientViewMode,
    hasFeature, hasModule, hasField, orgType, isAgenteAduanal,
    isViewingAs, viewAsOrgName, viewAsEditMode,
    toggleViewAsEdit, exitViewAs,
  } = useAuth();
  const { locale, changeLocale, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Banner appears ONLY when an actual super-admin/master-admin is in View-As mode.
  // Plain admins/clients can never see it, even if localStorage has stale flags.
  const isSuperAdminUser = !!(user?.is_super_admin || user?.is_master_admin);
  const showViewAsBanner = isSuperAdminUser && isViewingAs;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Home path based on role — clicking the OFIMEX logo returns to the main screen
  const homePath = isSuperAdminUser && !isViewingAs
    ? '/super-admin'
    : user?.role === 'client'
      ? '/my-dashboard'
      : '/dashboard';

  const handleExitViewAs = async () => {
    try {
      await exitViewAs();
      window.location.href = '/super-admin';
    } catch (e) {
      window.location.href = '/super-admin';
    }
  };

  const handleToggleEdit = async () => {
    try {
      await toggleViewAsEdit();
    } catch (e) {
      // ignore — banner will refresh via state update
    }
  };

  const isSuperAdmin = user?.is_super_admin === true || user?.is_master_admin === true;

  // Reorder nav items for Agente Aduanal: pedimentos/SOIA workflow first, then shipments
  // For Comercializadora (default): shipments-first workflow
  const orderedNavItems = (() => {
    if (isAgenteAduanal) {
      // AA priorities: clients/importers + pedimento workflow via shipments + tracking + certificates
      const priority = ['/clients', '/importers', '/shipments', '/tracking', '/certificates', '/alerts', '/users'];
      const ranked = [...navItems].sort((a, b) => {
        const ai = priority.indexOf(a.path);
        const bi = priority.indexOf(b.path);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
      return ranked;
    }
    return navItems;
  })();

  // When the super-admin is in View-As mode, render the SAME sidebar a regular
  // admin/ops would see for that org — that's the whole point of "incognito".
  // Treat them as 'admin' role for nav-item filtering since super-admins can see all features.
  const filteredNavItems = (isSuperAdmin && !isViewingAs)
    ? superAdminNavItems
    : orderedNavItems.filter(item => {
        const effectiveRole = isViewingAs ? 'admin' : user?.role;
        if (!item.roles.includes(effectiveRole)) return false;
        if (isViewingAs) {
          // Super-admin sees every feature/module/field while in View-As
          return true;
        }
        if (item.feature && !hasFeature(item.feature)) return false;
        if (item.module && !hasModule(item.module)) return false;
        if (item.field && !hasField(item.field)) return false;
        return true;
      });

  return (
    <div className="min-h-screen bg-background grain-overlay">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-header px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-secondary rounded-sm transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <Link to={homePath} aria-label="Ir al inicio" data-testid="logo-home-link-mobile">
          <img 
            src="/logo-ofimex-white.png" 
            alt="OFIMEX" 
            className="h-8 w-auto object-contain"
          />
        </Link>
        <NotificationBell />
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full bg-card border-r border-border z-50 transition-all duration-300 flex flex-col',
          sidebarOpen ? 'w-64' : 'w-16',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-border">
          {sidebarOpen ? (
            <Link to={homePath} aria-label="Ir al inicio" data-testid="logo-home-link-sidebar">
              <img 
                src="/logo-ofimex-white.png" 
                alt="OFIMEX" 
                className="h-8 w-auto object-contain"
              />
            </Link>
          ) : (
            <Link to={homePath} aria-label="Ir al inicio" data-testid="logo-home-link-sidebar-collapsed">
              <img 
                src="/logo-ofimex-white.png" 
                alt="OFIMEX" 
                className="h-6 w-auto object-contain"
              />
            </Link>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex p-1.5 hover:bg-secondary rounded-sm transition-colors"
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={cn('w-4 h-4 transition-transform', !sidebarOpen && 'rotate-180')} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-2 space-y-1 flex-1 overflow-y-auto">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-sm transition-colors',
                  isActive 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <Icon size={18} />
                {sidebarOpen && <span className="text-sm">{item.label || t(item.labelKey)}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Client View Toggle (for ops/admin) */}
        {canManage && !isSuperAdmin && sidebarOpen && (
          <div className="px-3 pb-2">
            <div className="flex items-center justify-between p-2 bg-secondary/50 rounded-sm">
              <div className="flex items-center gap-2 text-xs">
                {clientViewMode ? <Eye size={14} /> : <EyeOff size={14} />}
                <span>{t('nav.clientView')}</span>
              </div>
              <Switch
                checked={clientViewMode}
                onCheckedChange={setClientViewMode}
                data-testid="client-view-toggle"
              />
            </div>
          </div>
        )}

        {/* Language Toggle */}
        {sidebarOpen && (
          <div className="px-3 pb-2">
            <div className="flex items-center justify-center gap-1 p-0.5 bg-secondary/50 rounded-sm">
              <button
                onClick={() => changeLocale('es')}
                className={cn(
                  'flex-1 text-xs py-1 rounded-sm transition-colors font-medium',
                  locale === 'es' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
                data-testid="lang-es"
              >
                ES
              </button>
              <button
                onClick={() => changeLocale('en')}
                className={cn(
                  'flex-1 text-xs py-1 rounded-sm transition-colors font-medium',
                  locale === 'en' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
                data-testid="lang-en"
              >
                EN
              </button>
            </div>
          </div>
        )}

        {/* User info & logout */}
        <div className="p-3 border-t border-border">
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              <div className="text-xs flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-muted-foreground truncate">{user?.email}</p>
                {user?.organization_name && !isSuperAdmin && (
                  <p className="truncate mt-0.5">
                    <span className={cn(
                      'inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-medium',
                      isAgenteAduanal ? 'bg-amber-500/15 text-amber-400' : 'bg-blue-500/15 text-blue-400'
                    )} data-testid="org-type-badge">
                      {isAgenteAduanal ? 'AA' : 'COM'}
                    </span>
                    <span className="text-muted-foreground ml-1">{user.organization_name}</span>
                  </p>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="p-1.5 hover:bg-destructive/10 text-destructive rounded-sm transition-colors shrink-0"
                data-testid="logout-btn"
                title={t('nav.logout')}
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-destructive/10 text-destructive rounded-sm transition-colors w-full flex justify-center"
              data-testid="logout-btn-collapsed"
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main
        className={cn(
          'min-h-screen transition-all duration-300 pt-14 lg:pt-0',
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'
        )}
      >
        {/* View-As banner — visible ONLY for actual super-admins in incognito mode.
            Real customers (admin/ops/client) NEVER see this orange bar. */}
        {showViewAsBanner && (
          <div
            className={cn(
              'sticky top-0 z-50 px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm text-white',
              viewAsEditMode ? 'bg-red-600' : 'bg-amber-600'
            )}
            data-testid="view-as-banner"
          >
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-2">
                <span className="bg-white/20 px-2 py-0.5 rounded text-xs font-mono uppercase">
                  {viewAsEditMode ? 'Edición' : 'Solo lectura'}
                </span>
                <span>
                  Viendo como Super Admin · Org: <strong>{viewAsOrgName || '—'}</strong>
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleEdit}
                className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded font-medium"
                data-testid="view-as-toggle-edit-btn"
                title={viewAsEditMode ? 'Volver a solo lectura' : 'Activar modo edición'}
              >
                {viewAsEditMode ? 'Desactivar edición' : 'Activar modo edición'}
              </button>
              <button
                onClick={handleExitViewAs}
                className="bg-white text-amber-700 hover:bg-amber-50 px-3 py-1 rounded font-medium"
                data-testid="view-as-exit-btn"
              >
                Salir
              </button>
            </div>
          </div>
        )}
        <div className="p-4 md:p-6">
          {children}
        </div>
      </main>

      {/* Desktop-only floating notification bell (top-right) - hide while in View-As to keep
          the banner buttons clickable */}
      {!showViewAsBanner && (
        <div className="hidden lg:block fixed top-4 right-6 z-40">
          <NotificationBell />
        </div>
      )}

      {/* AI Chatbot */}
      <ChatBot />

      {/* First-login Onboarding Wizard */}
      <OnboardingWizard />
    </div>
  );
}
